import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "@reduxjs/toolkit";
import { repetitivePolling } from "../common/AsyncUtils";
import { RootState } from "../features/app/Store";
import { getTagsLatestAsync } from "../features/app/TagsAPI";
import { createScalarAndSpecificTagSelections } from "../features/app/TagQueryUtils";
import APP_CONFIG from "../features/app/configuration/AppConfig";
import { multiSiteTagsActions } from "../features/app/store/MultisiteTagsStore";
import { Tags } from "../features/data_point/tags/TagCatalog";
import { selectSiteId } from "../features/sites/SiteStore";

const gaugeTagNames = [
  Tags.Portal.Inverter.AcPower.aggregated,
  Tags.Portal.Weather.WindSpeed.aggregated,
  Tags.Portal.System.FieldState.logicalName,
  Tags.Portal.System.SafetyReason.logicalName,
  Tags.Portal.Inverter.SystemStatus.logicalName,
  Tags.Portal.TrackerStatus.SystemStatus.logicalName,
  Tags.Portal.Inverter.DcPower.aggregated,
  Tags.Portal.DailyProduction.AcPower.logicalName,
  Tags.Portal.DailyProduction.Sp.logicalName,
  Tags.Portal.PerformanceModel.PotentialPowerProduction.logicalName,
  Tags.Weather.GlobalIrradiance.aggregated,
  Tags.VirtualWeather.CalibratedGHI.logicalName,
  Tags.Weather.AmbientAirTemp.aggregated,
  Tags.Trackers.StateCount.Tracking.logicalName,
  Tags.Trackers.StateCount.Maintenance.logicalName,
  Tags.Agriculture.StemGrowth.aggregated,
  Tags.Agriculture.PlantPhotosyntheticallyActiveRadiationPpfd.aggregated,
  Tags.Weather.BarometricPressure.aggregated,
  Tags.Agriculture.RainFall.aggregated,
  Tags.Weather.RelativeHumidity.aggregated,
];

export async function pollLatestTagsOnce(
  dispatch: Dispatch,
  siteId: string,
  tagNamesToPoll: string[],
  signal: AbortSignal,
  getLatestTags: typeof getTagsLatestAsync = getTagsLatestAsync
): Promise<void> {
  if (signal.aborted) {
    return;
  }

  dispatch(multiSiteTagsActions.latestRequestStarted(siteId));
  try {
    const response = await getLatestTags(siteId, createScalarAndSpecificTagSelections(tagNamesToPoll));
    if (signal.aborted) {
      return;
    }
    dispatch(
      multiSiteTagsActions.updateSiteTags({
        siteId,
        data: response,
      })
    );
  } catch (error) {
    if (signal.aborted) {
      return;
    }
    dispatch(multiSiteTagsActions.latestRequestFailed(siteId));
    throw error;
  }
}

const TagsPoller = () => {
  const dispatch = useDispatch();
  const siteId = useSelector(selectSiteId);
  const tagsMetadata = useSelector((state: RootState) => state.multiSitesMetadata.sites[siteId]?.tags);

  useEffect(() => {
    if (!tagsMetadata) {
      return;
    }

    const tagNamesToPoll = gaugeTagNames.filter((tagName) => Object.hasOwn(tagsMetadata, tagName));

    if (!siteId || tagNamesToPoll.length == 0) {
      return;
    }

    const cntrlr = new AbortController();
    repetitivePolling(
      "pollTags",
      (signal) => pollLatestTagsOnce(dispatch, siteId, tagNamesToPoll, signal),
      APP_CONFIG.milliSecBetweenGetTags,
      APP_CONFIG.milliSecBetweenGetTags,
      cntrlr.signal
    );
    return () => {
      cntrlr.abort();
    };
  }, [siteId, tagsMetadata]);

  return <></>;
};

export default TagsPoller;
